<template>
	<div class="input-container">
		<small
			v-if="field_model.length"
			class="label"
		>
			{{ fieldText }}
		</small>
		<div
			:class="[{
				valid: !validate.loading && validate.isValid,
				error: showError,
			}]"
			class="input-box"
		>
			<input
				v-if="['firstName', 'lastName', 'year'].includes(field)"
				:id="field"
				:ref="field"
				v-model.trim="field_model"
				v-focus="focus"
				:placeholder="fieldPh"
				:type="inputType"
				autocomplete="off"
				class="form-control"
				autofocus
				@blur="onBlur"
				@input="onValueChange($event)"
			/>
			<input
				v-if="['email'].includes(field)"
				:id="field"
				:ref="field"
				v-model.trim="field_model"
				v-focus="focus"
				:placeholder="fieldPh"
				:type="inputType"
				autocomplete="off"
				class="form-control"
				autofocus
				@blur="onBlur"
				@keydown.space.prevent
				@input="onValueChange($event)"
			/>
			<masked-input
				v-else-if="field.includes('phone')"
				:id="field"
				:ref="field"
				v-model.trim="field_model"
				v-focus="focus"
				:disabled="disabled"
				:masked="false"
				:name="field"
				:placeholder="fieldPh"
				mask="(###) ### - ####"
				class="form-control"
				type="tel"
				autocomplete="off"
				autofocus
				@blur="onBlur"
				@input.native="onValueChange($event)"
			/>
			<div
				v-if="validate.touched"
				class="validation-indicator"
				item-right
			>
				<div
					:class="[(!validate.loading && 'load-complete')]"
					class="circle-loader"
				/>
				<div
					class="indicator-wrap"
				>
					<div
						:class="[!validate.loading && validate.isValid ? 'draw' : '']"
						class="checkmark"
					/>
					<div
						:class="[showError ? 'draw' : '']"
						class="line1"
					/>
					<div
						:class="[showError ? 'draw' : '']"
						class="line2"
					/>
				</div>
			</div>
		</div>
		<div
			v-if="showError"
			class="error-row"
		>
			{{ validate.error }}
		</div>
		<div
			v-else-if="field === 'email' && emailSuggestion"
			class="error-row"
		>
			<p>
				Did you mean:
				<a
					class="suggestion"
					@click="setSuggestedEmail"
				>
					{{ emailSuggestion }}
				</a>
			</p>
		</div>
		<!-- Empty space so the modal doesn't grow/shrink when error is displayed -->
		<div
			v-else
			:class="'blank-space' + (field.includes('phone') ? '-2x' : '')"
		/>
	</div>
</template>

<script>
import { mapState } from 'vuex';

import { TheMask } from 'vue-the-mask';

export default {
	name: 'Validate',

	components: {
		'masked-input': TheMask,
	},

	directives: {
		focus: {
			inserted: function(el, binding) {
				if (binding.value && !el.value.length) {
					el.focus();
				}
			},
		},
	},

	// eslint-disable-next-line
	props: {
		disabled: {
			type: Boolean,
			default: false,
		},
		field: {
			type: String,
			required: true,
		},
		fieldText: {
			type: String,
			default: '',
		},
		fieldPh: {
			type: String,
			default: '',
		},
		fieldValue: {
			type: String,
			required: true,
		},
		focus: {
			type: Boolean,
			default: false,
		},
		onChange: {
			type: Function,
			required: true,
		},
	},

	data: function() {
		return {
			currentYear: new Date().getFullYear(),
			touched: false,
			field_model: '',
		};
	},
	computed: {
		...mapState({
			quote: state => state.quote,
			validator: state => state.validator,
		}),

		emailSuggestion() {
			if (this.validate && this.validate.hasOwnProperty('suggested')) {
				return this.validate.suggested;
			}

			return '';
		},

		inputType() {
			if (this.field === 'year') {
				return 'number';
			} else if (this.field === 'email') {
				return 'email';
			}

			return 'text';
		},

		showError() {
			return this.validate.error
				&& !this.validate.loading
				&& !this.validate.isValid;
		},

		validate() {
			return this.validator[this.field];
		},
	},
	watch: {
		touched: {
			immediate: true,
			handler() {
				if (this.touched === true) {
					this.$store.commit('validator/SET_PROPERTY', {
						field: this.field,
						prop: 'touched',
						value: true,
					});
				}
			},
		},
		models: function() {
			if (!this.models.length) {
				this.model_text = 'Select Model';
			}
		},
	},
	created: function() {
		this.field_model = this.fieldValue;

		this.onValueChange = this.$iUtils.debounce(this.onValueChange, 750);

		if (this.field_model && this.field_model.length) {
			this.$store.commit('validator/RESET_FIELD', {
				field: this.field,
				value: this.field_model,
			});

			this.touched = true;
		}
	},
	methods: {
		setSuggestedEmail: function() {
			this.field_model = this.validate.suggested;
			this.onValueChange();
		},
		validateEmail: function() {
			const re = new RegExp(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);

			if (re.test(this.field_model)) {
				this.touched = true;
				this.validateField();
			}
		},
		validateYear: async function() {
			let isValidYear = false;

			this.touched = true;

			if (this.field_model > 1930 && this.field_model <= this.currentYear) {
				isValidYear = true;

				await this.$store.dispatch('quote/update', {
					year: this.field_model,
				});
			}

			this.$store.commit('validator/SET_VALID_STATE', {
				field: this.field,
				isValid: isValidYear,
			});

			this.afterValidation();
		},
		onBlur() {
			this.touched = true;
			this.onValueChange();
		},
		onValueChange(evt) {
			this.$log.debug(evt, this.validate, this.field_model);

			// inputType is only defined when user actually presses a key
			// inside the field, not if it was triggered through dispatchEvent
			if (evt && evt.inputType && this.validate.value === this.field_model) {
				return false;
			}

			this.$store.commit('validator/RESET_FIELD', {
				field: this.field,
				value: this.field_model,
			});

			this.$log.debug(this.field, this.validate);

			if (!this.field_model.length) {
				this.afterValidation();
			} else if (this.field_model.length >= 1) {
				if (this.field === 'email') {
					this.validateEmail();
				} else if (this.field.includes('phone')) {
					if (this.field_model.length > 9) {
						this.validateField();
					} else {
						this.buildMessage();
					}
				} else if (this.field === 'year') {
					this.validateYear();
				} else {
					this.validateField();
				}
			}
		},
		validateField: function() {
			let url = 'validate/';
			const model = String(this.field_model);
			const field = this.field;

			const validateData = {
				lead_token: this.quote.tokens.move,
				value: model,
				data: this.quote,
			};

			this.$log.debug('model->', model);

			this.$store.commit('validator/SET_PROPERTY', {
				field,
				prop: 'loading',
				value: true,
			});

			switch (field) {
				case 'firstName':
				case 'lastName':
					url += 'name';
					break;

				case 'retry_phone':
					url += 'phone';
					break;

				default:
					url += field;
					break;
			}

			this.$ireloApi
				.post(`/${url}`, validateData)
				.then(resp => {
					const data = resp.data;

					this.$store.commit('validator/SET_VALID_STATE', {
						field,
						data,
					});

					this.afterValidation();
				})
				.catch(errorResp => {
					this.$log.warn(errorResp);

					this.$store.commit('validator/SET_VALID_STATE', {
						field,
						data: errorResp,
					});

					this.afterValidation();
				});
		},
		afterValidation: function() {
			const value = String(this.field_model);

			this.$log.debug(value, this.validate);

			this.buildMessage();

			this.onChange(this.field, value, this.validate.isValid);
		},
		buildMessage: function() {
			let message = '';

			if (this.validate.touched && !this.validate.isValid) {
				if (!this.field.includes('phone')) {
					if (this.validate.value) {
						message = `Invalid ${this.fieldText}`;
					} else {
						message = `${this.fieldText} is required`;
					}
				} else if (!this.validate.value) {
					message = 'Phone number is required to get your quote.';
				} else if (this.validate.value < 10) {
					message = 'A 10-digit valid phone number is required to get your quote.';
				} else {
					message = 'Invalid phone number, please enter your active mobile number.';
				}
			}

			if (message) {
				this.$log.debug(message, this.validate.error);

				this.$store.dispatch('validator/invalidMessage', {
					field: this.field,
					message,
				});

				if (message === this.validate.error) {
					return;
				}

				this.$nextTick(() => {
					let el = this.$refs[this.field];

					if (!el) {
						return;
					}

					if (el.$el) {
						el = el.$el;
					}

					el.focus();
				});
			}
		},
		removeErrorMsg: function() {
			if (!this.validate.isValid) {
				this.touched = false;
				this.onChange(this.field, this.validate.value, this.validate.isValid);
			}
		},
		checkValidPhone: function() {
			if (this.field_model && this.field_model.length > 7) {
				this.onValueChange();
			}
		},
	},
};
</script>

<style lang="scss">
@import "./validation";
// Empty space so the modal doesn't grow/shrink when error is displayed
.blank-space {
	content: " ";
	height: 2rem;
	user-select: none;
	visibility: hidden;

	&-2x {
		height: 3.25rem;
	}
}

.input-box {
	background-color: rgba(100, 181, 217, 0.32);
	border-bottom: 1.5px solid #a7a7a7;
	border-top-left-radius: 4px;
	border-top-right-radius: 4px;
	display: inline-block;
	position: relative;
	text-align: left;
	width: 100%;

	&.focus {
		border-bottom: 2px solid #f39453;
	}

	&.error {
		border-bottom: 1.5px solid #d0021b;
	}

	&.valid {
		border-bottom: 1.5px solid #3bb671;
	}

	> * {
		display: inline-block !important;
	}

	.error {
		position: relative;
	}
}

.input-container {
	position: relative;

	.label {
		color: #a7a7a7;
		font-size: 14px;
		font-weight: bold;
		position: absolute;
		text-transform: capitalize;
		top: -25px;
	}
}

.error-row {
	color: #d0021b;
	font-size: 0.9rem;
	padding: 10px 0 0;
	text-transform: lowercase;

	&:first-letter {
		text-transform: uppercase;
	}

	.suggestion {
		color: #014c8c !important;
		cursor: pointer;
		text-decoration: underline !important;
		text-transform: lowercase;
	}
}
</style>
